import React, { useState, useRef, useEffect, useCallback } from 'react';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { uploadImageAndGetUrl, deleteImage, getSignedImageUrl, getBucketForCategory } from '../../../utils/imageUpload';
import { promotionsService } from '../../../services/artistContentService';
import { useAuth } from '../../../contexts/AuthContext';

// Homepage Promo Edit Modal Component
const HomepagePromoEditModal = ({ 
  isOpen, 
  onClose, 
  section, 
  title, 
  icon, 
  data, 
  onSave, 
  isUploading, 
  onImageUpload, 
  onImageRemove, 
  urlError 
}) => {
  const [formData, setFormData] = useState({
    featuredTitle: '',
    featuredSubtitle: '',
    buttonText: '',
    externalUrl: '',
    linkToExternal: false,
    ...data
  });

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && data) {
      setFormData({
        featuredTitle: data?.featuredTitle || '',
        featuredSubtitle: data?.featuredSubtitle || '',
        buttonText: data?.buttonText || '',
        externalUrl: data?.externalUrl || '',
        linkToExternal: data?.linkToExternal || false
      });
    }
  }, [isOpen, data]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    onSave(section, formData);
  };

  const handleImageSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      onImageUpload(section, file);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-surface rounded-lg border border-border p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
              <Icon name={icon} size={20} color="white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-text-primary">Edit {title} Promo</h2>
              <p className="text-sm text-text-secondary">Configure promotional content for the {title} section</p>
            </div>
          </div>
          
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Image Upload Section */}
        <div className="space-y-4 mb-6">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-text-primary">Promo Image</label>
            <span className="text-xs text-text-secondary">Optimal: 630x725px (6:7 ratio)</span>
          </div>
          
          <div className="relative border-2 border-dashed rounded-lg p-4 border-border hover:border-accent/50 transition-colors">
            {isUploading ? (
              <div className="text-center">
                <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-sm text-text-primary">Uploading...</p>
              </div>
            ) : urlError ? (
              <div className="text-center">
                <Icon name="AlertCircle" size={32} className="mx-auto text-destructive mb-2" />
                <p className="text-sm text-destructive mb-3">{urlError}</p>
                <Button variant="outline" size="sm" onClick={() => fileInputRef?.current?.click()}>
                  Upload New Image
                </Button>
              </div>
            ) : data?.image ? (
              <div className="relative">
                <div className="aspect-[6/7] max-w-xs mx-auto overflow-hidden rounded-lg">
                  <Image
                    src={data?.image}
                    alt={`${title} promo`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex justify-center mt-3 space-x-2">
                  <Button variant="outline" size="sm" onClick={() => fileInputRef?.current?.click()}>
                    Change Image
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={() => onImageRemove(section)}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <Icon name="Upload" size={32} className="mx-auto text-text-secondary mb-3" />
                <p className="text-sm text-text-primary mb-2">Click to upload promo image</p>
                <p className="text-xs text-text-secondary mb-3">JPG, PNG, WebP (Max 10MB)</p>
                <Button variant="outline" size="sm" onClick={() => fileInputRef?.current?.click()}>
                  Select Image
                </Button>
              </div>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
              disabled={isUploading}
            />
          </div>
        </div>
        
        {/* Content Fields */}
        <div className="space-y-4 mb-6">
          <Input
            label="Featured Title"
            value={formData?.featuredTitle}
            onChange={(e) => handleInputChange('featuredTitle', e?.target?.value)}
            placeholder="Enter featured title"
          />
          
          <Input
            label="Featured Subtitle"
            value={formData?.featuredSubtitle}
            onChange={(e) => handleInputChange('featuredSubtitle', e?.target?.value)}
            placeholder="Enter featured subtitle"
          />
          
          <Input
            label="Button Text"
            value={formData?.buttonText}
            onChange={(e) => handleInputChange('buttonText', e?.target?.value)}
            placeholder="e.g., Learn More, Shop Now"
          />
          
          <div className="space-y-3">
            <Checkbox
              label="Link to External URL"
              checked={formData?.linkToExternal}
              onChange={(checked) => handleInputChange('linkToExternal', checked)}
            />
            
            {formData?.linkToExternal && (
              <Input
                label="External URL"
                value={formData?.externalUrl}
                onChange={(e) => handleInputChange('externalUrl', e?.target?.value)}
                placeholder="https://example.com"
                type="url"
              />
            )}
          </div>
        </div>
        
        {/* Modal Actions */}
        <div className="flex justify-end space-x-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="default" onClick={handleSave}>
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

const PromotionSection = ({ artistData, onUpdate, isActive }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    splashImage: null,
    splashImagePath: null,
    homeImage: null,
    homeImagePath: null,
    logo: null,
    logoPath: null,
    featuredItem: ""
  });

  // Homepage promos state
  const [homepagePromos, setHomepagePromos] = useState({
    calendar: {
      image: null,
      imagePath: null,
      featuredTitle: '',
      featuredSubtitle: '',
      buttonText: '',
      externalUrl: '',
      linkToExternal: false
    },
    work: {
      image: null,
      imagePath: null,
      featuredTitle: '',
      featuredSubtitle: '',
      buttonText: '',
      externalUrl: '',
      linkToExternal: false
    },
    shop: {
      image: null,
      imagePath: null,
      featuredTitle: '',
      featuredSubtitle: '',
      buttonText: '',
      externalUrl: '',
      linkToExternal: false
    },
    social: {
      image: null,
      imagePath: null,
      featuredTitle: '',
      featuredSubtitle: '',
      buttonText: '',
      externalUrl: '',
      linkToExternal: false
    }
  });

  // Modal state for editing individual promos
  const [activeModal, setActiveModal] = useState(null);

  const [dragStates, setDragStates] = useState({
    splash: false,
    home: false,
    logo: false,
    calendar: false,
    work: false,
    shop: false,
    social: false
  });

  const [uploadStates, setUploadStates] = useState({
    splash: false,
    home: false,
    logo: false,
    calendar: false,
    work: false,
    shop: false,
    social: false
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [urlErrors, setUrlErrors] = useState({});

  const splashRef = useRef(null);
  const homeRef = useRef(null);
  const logoRef = useRef(null);

  // Define promo sections with their icons
  const promoSections = [
    {
      key: 'calendar',
      title: 'Calendar',
      icon: 'Calendar',
      description: 'Events and performances'
    },
    {
      key: 'work',
      title: 'Work',
      icon: 'Briefcase',
      description: 'Portfolio and artworks'
    },
    {
      key: 'shop',
      title: 'Shop',
      icon: 'ShoppingBag',
      description: 'Merchandise and products'
    },
    {
      key: 'social',
      title: 'Social',
      icon: 'Users',
      description: 'Social media and community'
    }
  ];

  // Load promotion content on component mount
  useEffect(() => {
    if (user?.id) {
      loadPromotionContent();
    }
  }, [user?.id]);

  const loadPromotionContent = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      // Load basic promotion data
      const { data, error } = await promotionsService?.getPromotionContent(user?.id);
      
      if (error && error?.code !== 'PGRST116') {
        console.error('Error loading promotion content:', error);
        return;
      }

      if (data) {
        // Enhanced image URL generation with better error handling
        const bucket = getBucketForCategory('promotion');
        const imageFields = [
          { pathKey: 'splash_image_path', urlKey: 'splashImage', localPathKey: 'splashImagePath', type: 'splash' },
          { pathKey: 'home_image_path', urlKey: 'homeImage', localPathKey: 'homeImagePath', type: 'home' },
          { pathKey: 'logo_image_path', urlKey: 'logo', localPathKey: 'logoPath', type: 'logo' }
        ];

        const updatedData = {
          featuredItem: data?.featured_item || ""
        };

        const newUrlErrors = {};

        // Process existing image fields
        for (const field of imageFields) {
          if (data?.[field?.pathKey]) {
            try {
              // Enhanced signed URL generation with fallback strategies
              const urlResult = await generateSignedUrlWithFallback(bucket, data?.[field?.pathKey], field?.type);
              
              if (urlResult?.success) {
                updatedData[field?.urlKey] = urlResult?.url;
                updatedData[field?.localPathKey] = data?.[field?.pathKey];
              } else {
                updatedData[field?.urlKey] = null;
                updatedData[field?.localPathKey] = data?.[field?.pathKey];
                newUrlErrors[field.type] = urlResult?.error || 'Failed to load image';
                console.warn(`Failed to generate URL for ${field?.type}:`, urlResult?.error);
              }
            } catch (error) {
              updatedData[field?.urlKey] = null;
              updatedData[field?.localPathKey] = data?.[field?.pathKey];
              newUrlErrors[field.type] = 'Error loading image';
              console.error(`Error processing ${field?.type} image:`, error);
            }
          } else {
            updatedData[field?.urlKey] = null;
            updatedData[field?.localPathKey] = null;
          }
        }

        setFormData(updatedData);
        setUrlErrors(newUrlErrors);
      }

      // Load homepage promos separately
      const { data: promosData, error: promosError } = await promotionsService?.getHomepagePromos(user?.id);
      
      if (promosData && !promosError) {
        const loadedPromos = { ...homepagePromos };
        
        // Process each promo section
        for (const [section, promoData] of Object.entries(promosData)) {
          if (loadedPromos?.[section]) {
            loadedPromos[section] = {
              ...loadedPromos?.[section],
              featuredTitle: promoData?.featured_title || '',
              featuredSubtitle: promoData?.featured_subtitle || '',
              buttonText: promoData?.button_text || '',
              externalUrl: promoData?.external_url || '',
              linkToExternal: promoData?.link_to_external || false,
              imagePath: promoData?.image_path || null
            };

            // Load image if path exists
            if (promoData?.image_path) {
              try {
                const bucket = getBucketForCategory('promotion');
                const urlResult = await generateSignedUrlWithFallback(bucket, promoData?.image_path, section);
                
                if (urlResult?.success) {
                  loadedPromos[section].image = urlResult?.url;
                } else {
                  setUrlErrors(prev => ({ ...prev, [section]: urlResult?.error }));
                }
              } catch (error) {
                setUrlErrors(prev => ({ ...prev, [section]: 'Error loading homepage promo image' }));
              }
            }
          }
        }
        
        setHomepagePromos(loadedPromos);
      }
    } catch (error) {
      console.error('Error loading promotion content:', error);
      setSaveError('Failed to load promotion content. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  // Enhanced signed URL generation with multiple fallback strategies
  const generateSignedUrlWithFallback = async (bucket, filePath, imageType, maxRetries = 3) => {
    if (!filePath) {
      return { success: false, error: 'No file path provided' };
    }

    // Try multiple expiry times and retry strategies
    const expiryOptions = [7200, 3600, 1800]; // 2h, 1h, 30min
    
    for (let retryCount = 0; retryCount < maxRetries; retryCount++) {
      for (const expirySeconds of expiryOptions) {
        try {
          const result = await getSignedImageUrl(bucket, filePath, expirySeconds);
          
          if (result?.data?.signedUrl) {
            console.log(`Successfully generated URL for ${imageType} on retry ${retryCount + 1} with ${expirySeconds}s expiry`);
            return { 
              success: true, 
              url: result?.data?.signedUrl,
              expiry: expirySeconds 
            };
          }
          
          // If this was an "Object not found" error, the file doesn't exist
          if (result?.error?.includes('not found') || result?.error?.includes('does not exist')) {
            console.warn(`Image file not found in storage for ${imageType}: ${filePath}`);
            return { 
              success: false, 
              error: `Image not found in storage. Please re-upload the ${imageType} image.`
            };
          }
          
        } catch (error) {
          console.warn(`URL generation attempt failed for ${imageType}:`, error?.message);
        }
        
        // Small delay between expiry attempts
        if (expiryOptions?.indexOf(expirySeconds) < expiryOptions?.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
      
      // Longer delay between retry attempts
      if (retryCount < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
      }
    }
    
    return { 
      success: false, 
      error: `Failed to load ${imageType} image after ${maxRetries} attempts. The image may need to be re-uploaded.`
    };
  };

  // Handle homepage promo modal save
  const handlePromoModalSave = async (section, formData) => {
    const updatedPromos = {
      ...homepagePromos,
      [section]: {
        ...homepagePromos?.[section],
        featuredTitle: formData?.featuredTitle,
        featuredSubtitle: formData?.featuredSubtitle,
        buttonText: formData?.buttonText,
        externalUrl: formData?.externalUrl,
        linkToExternal: formData?.linkToExternal
      }
    };

    setHomepagePromos(updatedPromos);

    // Auto-save to database
    try {
      await saveHomepagePromosToDatabase(updatedPromos);
      console.log(`${section} homepage promo updated successfully`);
      setActiveModal(null); // Close modal
    } catch (error) {
      console.error('Failed to save homepage promo:', error);
      setSaveError('Failed to save changes. Please try again.');
    }
  };

  // Handle homepage promo image upload
  const handleHomepagePromoImageUpload = async (section, file) => {
    if (!file || !file?.type?.startsWith('image/') || !user?.id) {
      return;
    }

    setUploadStates(prev => ({ ...prev, [section]: true }));
    setSaveError('');
    setUrlErrors(prev => ({ ...prev, [section]: null }));

    try {
      // Delete old image if it exists
      if (homepagePromos?.[section]?.imagePath) {
        const bucket = getBucketForCategory('promotion');
        await deleteImage(bucket, homepagePromos?.[section]?.imagePath);
      }

      // Upload new image
      const result = await uploadImageAndGetUrl(file, user?.id, 'promotion');
      
      if (result?.error) {
        setSaveError(`Upload failed: ${result?.error}`);
        return;
      }

      if (!result?.data?.path) {
        setSaveError('Upload failed: No file path returned');
        return;
      }

      // Update homepage promos state with new image
      const updatedPromos = {
        ...homepagePromos,
        [section]: {
          ...homepagePromos?.[section],
          image: result?.data?.url,
          imagePath: result?.data?.path
        }
      };

      setHomepagePromos(updatedPromos);

      // Auto-save to database
      try {
        await saveHomepagePromosToDatabase(updatedPromos);
        console.log(`${section} homepage promo image auto-saved to database successfully`);
      } catch (saveError) {
        console.error('Auto-save failed:', saveError);
        setSaveError('Image uploaded successfully but auto-save failed. Please click Save Changes.');
      }

    } catch (error) {
      setSaveError('Failed to upload homepage promo image');
      console.error('Homepage promo image upload error:', error);
    } finally {
      setUploadStates(prev => ({ ...prev, [section]: false }));
    }
  };

  // Save homepage promos to database
  const saveHomepagePromosToDatabase = async (promosData) => {
    const dbFormatPromos = {};
    
    for (const [section, data] of Object.entries(promosData)) {
      dbFormatPromos[section] = {
        image_path: data?.imagePath,
        featured_title: data?.featuredTitle,
        featured_subtitle: data?.featuredSubtitle,
        button_text: data?.buttonText,
        external_url: data?.externalUrl,
        link_to_external: data?.linkToExternal
      };
    }

    const { data, error } = await promotionsService?.saveHomepagePromos(user?.id, dbFormatPromos);
    
    if (error) {
      throw new Error(`Database save failed: ${error?.message}`);
    }
    
    return data;
  };

  // Remove homepage promo image
  const removeHomepagePromoImage = async (section) => {
    // Delete from storage if path exists
    if (homepagePromos?.[section]?.imagePath) {
      const bucket = getBucketForCategory('promotion');
      await deleteImage(bucket, homepagePromos?.[section]?.imagePath);
    }
    
    const updatedPromos = {
      ...homepagePromos,
      [section]: {
        ...homepagePromos?.[section],
        image: null,
        imagePath: null
      }
    };

    setHomepagePromos(updatedPromos);
    setUrlErrors(prev => ({ ...prev, [section]: null }));

    // Auto-save removal to database
    try {
      await saveHomepagePromosToDatabase(updatedPromos);
      console.log(`${section} homepage promo image removal auto-saved to database`);
    } catch (error) {
      console.error('Auto-save removal failed:', error);
    }
  };

  const handleImageUpload = async (type, file) => {
    if (!file || !file?.type?.startsWith('image/') || !user?.id) {
      return;
    }

    setUploadStates(prev => ({ ...prev, [type]: true }));
    setSaveError('');
    setUrlErrors(prev => ({ ...prev, [type]: null })); // Clear any previous errors

    try {
      // Delete old image if it exists
      let pathKey = `${type}ImagePath`;
      if (type === 'splash') {
        pathKey = 'splashImagePath';
      } else if (type === 'home') {
        pathKey = 'homeImagePath';
      } else if (type === 'logo') {
        pathKey = 'logoPath';
      }

      if (formData?.[pathKey]) {
        const bucket = getBucketForCategory('promotion');
        await deleteImage(bucket, formData?.[pathKey]);
      }

      // Upload new image with enhanced verification
      const result = await uploadImageAndGetUrl(file, user?.id, 'promotion');
      
      if (result?.error) {
        setSaveError(`Upload failed: ${result?.error}`);
        return;
      }

      if (!result?.data?.path) {
        setSaveError('Upload failed: No file path returned');
        return;
      }

      // Update form data with new image URL and path
      const imageKey = type === 'splash' ? 'splashImage' : type === 'home' ? 'homeImage' : 'logo';
      const pathKeyFinal = type === 'splash' ? 'splashImagePath' : type === 'home' ? 'homeImagePath' : 'logoPath';
      
      const updatedFormData = {
        ...formData,
        [imageKey]: result?.data?.url,
        [pathKeyFinal]: result?.data?.path
      };

      setFormData(updatedFormData);

      // Auto-save to database immediately after successful upload
      try {
        const saveData = {
          splashImagePath: type === 'splash' ? result?.data?.path : formData?.splashImagePath,
          homeImagePath: type === 'home' ? result?.data?.path : formData?.homeImagePath,
          logoImagePath: type === 'logo' ? result?.data?.path : formData?.logoPath,
          featuredItem: formData?.featuredItem
        };

        const { data, error } = await promotionsService?.savePromotionContent(user?.id, saveData);

        if (error) {
          setSaveError(`Image uploaded but database save failed: ${error?.message}`);
        } else {
          console.log(`${type} image auto-saved to database successfully`);
          
          // If URL wasn't generated during upload, try to generate it now
          if (!result?.data?.url && result?.data?.path) {
            const bucket = getBucketForCategory('promotion');
            const urlResult = await generateSignedUrlWithFallback(bucket, result?.data?.path, type);
            
            if (urlResult?.success) {
              setFormData(prev => ({ ...prev, [imageKey]: urlResult?.url }));
            } else {
              setUrlErrors(prev => ({ ...prev, [type]: urlResult?.error }));
            }
          }
        }
      } catch (saveError) {
        console.error('Auto-save failed:', saveError);
        setSaveError('Image uploaded successfully but auto-save failed. Please click Save Changes.');
      }

    } catch (error) {
      setSaveError('Failed to upload image');
      console.error('Image upload error:', error);
    } finally {
      setUploadStates(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleDragOver = (e, type) => {
    e?.preventDefault();
    setDragStates(prev => ({ ...prev, [type]: true }));
  };

  const handleDragLeave = (e, type) => {
    e?.preventDefault();
    setDragStates(prev => ({ ...prev, [type]: false }));
  };

  const handleDrop = (e, type) => {
    e?.preventDefault();
    setDragStates(prev => ({ ...prev, [type]: false }));
    
    const files = e?.dataTransfer?.files;
    if (files?.length > 0) {
      handleImageUpload(type, files?.[0]);
    }
  };

  const handleFileSelect = (type, e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      handleImageUpload(type, file);
    }
  };

  const removeImage = async (type) => {
    const imageKey = type === 'splash' ? 'splashImage' : type === 'home' ? 'homeImage' : 'logo';
    let pathKey = type === 'splash' ? 'splashImagePath' : type === 'home' ? 'homeImagePath' : 'logoPath';
    
    // Delete from storage if path exists
    if (formData?.[pathKey]) {
      const bucket = getBucketForCategory('promotion');
      await deleteImage(bucket, formData?.[pathKey]);
    }
    
    const updatedFormData = {
      ...formData,
      [imageKey]: null,
      [pathKey]: null
    };

    setFormData(updatedFormData);
    setUrlErrors(prev => ({ ...prev, [type]: null })); // Clear error for this image

    // Auto-save removal to database
    try {
      await promotionsService?.savePromotionContent(user?.id, {
        splashImagePath: type === 'splash' ? null : formData?.splashImagePath,
        homeImagePath: type === 'home' ? null : formData?.homeImagePath,
        logoImagePath: type === 'logo' ? null : formData?.logoPath,
        featuredItem: formData?.featuredItem
      });
      console.log(`${type} image removal auto-saved to database`);
    } catch (error) {
      console.error('Auto-save removal failed:', error);
    }
  };

  const handleSave = async () => {
    if (!user?.id) return;

    setSaving(true);
    setSaveError('');

    try {
      // Save basic promotion data
      const { data, error } = await promotionsService?.savePromotionContent(user?.id, {
        splashImagePath: formData?.splashImagePath,
        homeImagePath: formData?.homeImagePath,
        logoImagePath: formData?.logoPath,
        featuredItem: formData?.featuredItem
      });

      if (error) {
        setSaveError(`Save failed: ${error?.message}`);
        return;
      }

      // Save homepage promos
      await saveHomepagePromosToDatabase(homepagePromos);

      // Force refresh of signed URLs to ensure they're current and fix any broken ones
      const updatedData = { ...formData };
      const bucket = getBucketForCategory('promotion');
      const newUrlErrors = {};
      
      const imageRefreshTasks = [
        { type: 'splash', pathKey: 'splashImagePath', urlKey: 'splashImage' },
        { type: 'home', pathKey: 'homeImagePath', urlKey: 'homeImage' },
        { type: 'logo', pathKey: 'logoPath', urlKey: 'logo' }
      ];

      for (const task of imageRefreshTasks) {
        if (updatedData?.[task?.pathKey]) {
          try {
            const urlResult = await generateSignedUrlWithFallback(bucket, updatedData?.[task?.pathKey], task?.type);
            
            if (urlResult?.success) {
              updatedData[task.urlKey] = urlResult?.url;
            } else {
              console.warn(`Failed to refresh URL for ${task?.type}:`, urlResult?.error);
              newUrlErrors[task.type] = urlResult?.error;
              // Keep existing URL if refresh fails
            }
          } catch (urlError) {
            console.warn(`Error refreshing URL for ${task?.type}:`, urlError);
            newUrlErrors[task.type] = 'Error refreshing image';
          }
        }
      }

      setFormData(updatedData);
      setUrlErrors(newUrlErrors);

      // Update parent component if needed
      onUpdate?.('promotion', { ...formData, homepagePromos });
      
      console.log('Promotion content and homepage promos saved successfully');

    } catch (error) {
      setSaveError('Failed to save promotion content');
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  // Enhanced retry function for failed image loads
  const retryImageLoad = async (type) => {
    let pathKey = type === 'splash' ? 'splashImagePath' : type === 'home' ? 'homeImagePath' : 'logoPath';
    const imageKey = type === 'splash' ? 'splashImage' : type === 'home' ? 'homeImage' : 'logo';
    
    if (!formData?.[pathKey]) return;

    setUrlErrors(prev => ({ ...prev, [type]: null }));
    
    try {
      const bucket = getBucketForCategory('promotion');
      const urlResult = await generateSignedUrlWithFallback(bucket, formData?.[pathKey], type);
      
      if (urlResult?.success) {
        setFormData(prev => ({ ...prev, [imageKey]: urlResult?.url }));
      } else {
        setUrlErrors(prev => ({ ...prev, [type]: urlResult?.error }));
      }
    } catch (error) {
      setUrlErrors(prev => ({ ...prev, [type]: 'Failed to reload image' }));
    }
  };

  const ImageUploadArea = ({ type, title, dimensions, currentImage, fileRef, isUploading }) => {
    const hasError = urlErrors?.[type];
    const hasPath = type === 'splash' ? formData?.splashImagePath : 
                   type === 'home' ? formData?.homeImagePath : 
                   formData?.logoPath;

    // Determine aspect ratio class based on type
    const getAspectRatioClass = () => {
      switch(type) {
        case 'splash':
          return 'aspect-[9/16]'; // 9:16 vertical ratio for mobile splash
        case 'home':
          return 'aspect-[9/16]'; // 9:16 vertical ratio for home background
        case 'logo':
          return 'aspect-square'; // 1:1 square ratio for logo
        default:
          return 'h-32';
      }
    };

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-text-primary">{title}</h4>
          <span className="text-xs text-text-secondary">{dimensions}</span>
        </div>
        
        <div
          className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
            dragStates?.[type] 
              ? 'border-accent bg-accent/10' 
              : hasError 
                ? 'border-destructive bg-destructive/5' :'border-border hover:border-accent/50'
          } ${isUploading ? 'pointer-events-none opacity-50' : ''}`}
          onDragOver={(e) => handleDragOver(e, type)}
          onDragLeave={(e) => handleDragLeave(e, type)}
          onDrop={(e) => handleDrop(e, type)}
        >
          {isUploading ? (
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-sm text-text-primary">Uploading...</p>
            </div>
          ) : hasError && hasPath ? (
            <div className="text-center">
              <Icon name="AlertCircle" size={32} className="mx-auto text-destructive mb-2" />
              <p className="text-sm text-destructive mb-2">{hasError}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => retryImageLoad(type)}
                className="mb-2"
              >
                Retry Load
              </Button>
              <p className="text-xs text-text-secondary">
                Or upload a new image below
              </p>
            </div>
          ) : currentImage ? (
            <div className="relative">
              <div className={`${getAspectRatioClass()} overflow-hidden rounded-lg`}>
                <Image
                  src={currentImage}
                  alt={title}
                  className="w-full h-full object-cover"
                />
              </div>
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2"
                onClick={() => removeImage(type)}
                disabled={isUploading}
              >
                <Icon name="X" size={16} />
              </Button>
            </div>
          ) : (
            <div className="text-center">
              <Icon name="Upload" size={32} className="mx-auto text-text-secondary mb-2" />
              <p className="text-sm text-text-primary mb-1">
                Drop your image here or click to browse
              </p>
              <p className="text-xs text-text-secondary">
                Supports: JPG, PNG, WebP (Max 10MB)
              </p>
              <p className="text-xs text-accent mt-1">
                Optimal ratio: {dimensions}
              </p>
            </div>
          )}
          
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFileSelect(type, e)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={isUploading}
          />
        </div>
      </div>
    );
  };

  if (!isActive) return null;

  if (loading) {
    return (
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-text-secondary">Loading promotion content...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
            <Icon name="Megaphone" size={20} color="white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Promotion Assets</h2>
            <p className="text-sm text-text-secondary">Upload images and select featured content for your mobile app</p>
          </div>
        </div>
        
        <Button 
          variant="default" 
          size="sm" 
          onClick={handleSave}
          disabled={Object.values(uploadStates)?.some(Boolean) || saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {saveError && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-sm text-destructive">{saveError}</p>
        </div>
      )}

      {/* Enhanced status indicators for image loading issues */}
      {Object.keys(urlErrors)?.length > 0 && (
        <div className="mb-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="AlertTriangle" size={16} className="text-warning" />
            <p className="text-sm text-warning font-medium">Some images need attention:</p>
          </div>
          <ul className="text-xs text-warning space-y-1">
            {Object.entries(urlErrors)?.map(([type, error]) => (
              <li key={type}>• {type}: {error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Splash Screen Image */}
        <ImageUploadArea
          type="splash"
          title="Splash Screen"
          dimensions="9:16 ratio (e.g., 1080×1920px)"
          currentImage={formData?.splashImage}
          fileRef={splashRef}
          isUploading={uploadStates?.splash}
        />

        {/* Home Screen Image */}
        <ImageUploadArea
          type="home"
          title="Home Screen"
          dimensions="9:16 ratio (e.g., 1080×1920px)"
          currentImage={formData?.homeImage}
          fileRef={homeRef}
          isUploading={uploadStates?.home}
        />

        {/* Logo */}
        <ImageUploadArea
          type="logo"
          title="App Logo"
          dimensions="1:1 ratio (e.g., 512×512px)"
          currentImage={formData?.logo}
          fileRef={logoRef}
          isUploading={uploadStates?.logo}
        />
      </div>

      {/* New Homepage Promos Section with Icons */}
      <div className="border-t border-border pt-8 mb-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
            <Icon name="Layout" size={20} color="white" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-text-primary">Homepage Promos</h3>
            <p className="text-sm text-text-secondary">Click on the icons below to edit promotional content for each section</p>
          </div>
        </div>
        
        {/* Icons Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {promoSections?.map((section) => {
            const promoData = homepagePromos?.[section?.key];
            const hasContent = promoData?.featuredTitle || promoData?.image;
            const hasError = urlErrors?.[section?.key];
            
            return (
              <div
                key={section?.key}
                className={`relative p-6 rounded-lg border-2 border-dashed cursor-pointer transition-all duration-200 hover:border-accent/50 hover:bg-accent/5 ${
                  hasError 
                    ? 'border-destructive bg-destructive/5' 
                    : hasContent 
                      ? 'border-accent bg-accent/10' :'border-border'
                }`}
                onClick={() => setActiveModal(section?.key)}
              >
                <div className="text-center">
                  {/* Status indicator */}
                  <div className="relative mb-3">
                    <div className={`w-16 h-16 mx-auto rounded-lg flex items-center justify-center ${
                      hasError 
                        ? 'bg-destructive text-white' 
                        : hasContent 
                          ? 'bg-accent text-white' :'bg-muted text-text-secondary'
                    }`}>
                      <Icon name={section?.icon} size={24} />
                    </div>
                    
                    {/* Content indicator dot */}
                    {hasContent && !hasError && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-success rounded-full border-2 border-surface flex items-center justify-center">
                        <Icon name="Check" size={10} color="white" />
                      </div>
                    )}
                    
                    {/* Error indicator */}
                    {hasError && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-destructive rounded-full border-2 border-surface flex items-center justify-center">
                        <Icon name="AlertCircle" size={10} color="white" />
                      </div>
                    )}
                  </div>
                  
                  <h4 className="text-lg font-medium text-text-primary mb-1">{section?.title}</h4>
                  <p className="text-xs text-text-secondary mb-3">{section?.description}</p>
                  
                  {/* Status text */}
                  <div className="text-xs">
                    {hasError ? (
                      <span className="text-destructive">Needs attention</span>
                    ) : hasContent ? (
                      <span className="text-success">Configured</span>
                    ) : (
                      <span className="text-text-secondary">Click to configure</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Homepage Promo Edit Modal */}
      {activeModal && (
        <HomepagePromoEditModal
          isOpen={true}
          onClose={() => setActiveModal(null)}
          section={activeModal}
          title={promoSections?.find(s => s?.key === activeModal)?.title}
          icon={promoSections?.find(s => s?.key === activeModal)?.icon}
          data={homepagePromos?.[activeModal]}
          onSave={handlePromoModalSave}
          isUploading={uploadStates?.[activeModal]}
          onImageUpload={handleHomepagePromoImageUpload}
          onImageRemove={removeHomepagePromoImage}
          urlError={urlErrors?.[activeModal]}
        />
      )}

      {/* Enhanced Status Indicator */}
      <div className="mt-6 p-4 bg-muted rounded-lg">
        <div className="flex items-center space-x-2">
          <Icon 
            name={formData?.splashImage && formData?.logo ? "CheckCircle" : "AlertCircle"} 
            size={16} 
            className={formData?.splashImage && formData?.logo ? "text-success" : "text-warning"}
          />
          <span className="text-sm font-medium text-text-primary">
            Promotion Status: {formData?.splashImage && formData?.logo ? "Complete" : "Incomplete"}
          </span>
        </div>
        {(!formData?.splashImage || !formData?.logo) && (
          <p className="text-sm text-text-secondary mt-1">
            Splash screen image and logo are required for app launch.
          </p>
        )}
        {Object.keys(urlErrors)?.length > 0 && (
          <p className="text-sm text-warning mt-1">
            Some images have loading issues. Try the "Retry Load" button or re-upload the affected images.
          </p>
        )}
      </div>
    </div>
  );
};

export default PromotionSection;